import { StageFilterBar, usePersistedFlag, filterByStage, StageKey } from '../common/StageFilterBar.tsx';
import { ProjectMultiSelect } from '../common/ProjectMultiSelect.tsx';
import { TeamSelect, useTeamMemberIds, taskInTeams } from '../common/TeamSelect.tsx';
import React, { useState, useEffect, useMemo } from 'react';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import {
  FolderKanban,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Users,
  ArrowRight,
  Sparkles,
  LayoutGrid,
  Plus,
  Video,
  FileText,
  PhoneCall,
  Check,
  Search,
  ChevronRight,
  TrendingUp,
  List,
  Calendar as CalendarIcon,
  Layout,
  ChevronDown,
  X,
  Radio,
  ExternalLink,
  FileSpreadsheet,
  UserPlus,
  Loader2,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { DashboardStats, Task, TaskStatus, Project, User } from '../../../shared/types.ts';
import { PriorityBadge, StatusBadge } from '../common/Badge.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { Modal } from '../common/Modal.tsx';
import { KanbanBoard } from '../projects/KanbanBoard.tsx';
import { TaskTable } from '../projects/TaskTable.tsx';
import { TimelineView } from '../projects/TimelineView.tsx';
import { WhiteboardView } from '../projects/WhiteboardView.tsx';
import { CalendarView } from '../projects/CalendarView.tsx';
import { ExistingProjectMembersModal } from '../projects/ExistingProjectMembersModal.tsx';
import { CreateTaskModal } from '../tasks/CreateTaskModal.tsx';
import { ImportTasksModal } from '../tasks/ImportTasksModal.tsx';
import { useCall } from '../../context/CallContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';

interface CompanyDashboardProps {
  onNavigateToProject: (projectId: string) => void;
  onOpenTask: (taskId: string, projectId?: string) => void;
  onNavigate: (view: string) => void;
}

export const CompanyDashboard: React.FC<CompanyDashboardProps> = ({
  onNavigateToProject,
  onOpenTask,
  onNavigate,
}) => {
  const { token, user } = useAuth();
  const { joinCall } = useCall();
  const { subscribe } = useRealtime();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Kanban / Multi-View Board state
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  // Projects shown on the board: none picked = all, one or several picked = just those.
  // `selectedProjectId` stays the single project when exactly one is picked ('all' otherwise), so the
  // single-project features (members, new task defaults...) keep working unchanged.
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const teamMemberIds = useTeamMemberIds(selectedTeamIds);
  const selectedProjectId: string = selectedProjectIds.length === 1 ? selectedProjectIds[0] : 'all';
  const selectedProjectsKey = selectedProjectIds.join(',');
  const matchesProjectSelection = (projectId?: string | null) =>
    selectedProjectIds.length === 0 || (!!projectId && selectedProjectIds.includes(projectId));
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [dashboardViewType, setDashboardViewType] = useState<
    'kanban' | 'list' | 'timeline' | 'whiteboard' | 'calendar'
  >('kanban');
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isImportTasksOpen, setIsImportTasksOpen] = useState(false);
  const [quickCreateStatus, setQuickCreateStatus] = useState<TaskStatus>('todo');

  // Next Upcoming Meeting state
  const [nextMeeting, setNextMeeting] = useState<any>(null);

  // Role-adaptive metric filter state: 'all' | 'active' | 'completed' | 'focus'
  const [metricFilter, setMetricFilter] = useState<'all' | 'active' | 'completed' | 'focus'>('all');

  // Show/hide the Overdue and Completed sections on the board (remembered per browser)
  const [hideOverdue, toggleOverdueFlag] = usePersistedFlag('pulse_hide_overdue');
  const [hideCompleted, toggleCompletedFlag] = usePersistedFlag('pulse_hide_completed');
  const toggleHideOverdue = () => {
    // The stage filter disappears with its section, so drop it when hiding
    if (!hideOverdue && metricFilter === 'focus') setMetricFilter('all');
    toggleOverdueFlag();
  };
  const toggleHideCompleted = () => {
    if (!hideCompleted && metricFilter === 'completed') setMetricFilter('all');
    toggleCompletedFlag();
  };

  // Project members & Management state (TSK-1038 & TSK-1039)
  const [projects, setProjects] = useState<Project[]>([]);
  const [isViewMembersModalOpen, setIsViewMembersModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [isLoadingAllUsers, setIsLoadingAllUsers] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [memberError, setMemberError] = useState<string | null>(null);
  const [isManagingMemberId, setIsManagingMemberId] = useState<string | null>(null);

  // Who may manage project members is set on the Roles & Access screen
  const { can } = usePermissions();
  const isLead = can('project.members');

  const isOverdue = (dueDateStr?: string | null) => {
    if (!dueDateStr) return false;
    const nowStr = new Date().toISOString().split('T')[0];
    return String(dueDateStr).slice(0, 10) < nowStr;
  };

  const fetchProjects = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/projects?status=active&allSpaces=true', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } catch (err) {
      console.error('Failed to fetch projects in dashboard:', err);
    }
  };

  const fetchUsers = async () => {
    if (!token) return;
    setIsLoadingAllUsers(true);
    try {
      const res = await fetch('/api/team/members', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setAllUsers(data.members || []);
      }
    } catch (err) {
      console.error('Failed to fetch team members:', err);
    } finally {
      setIsLoadingAllUsers(false);
    }
  };

  const handleAddProjectMember = async (userId: string) => {
    if (!selectedProjectId || selectedProjectId === 'all' || !token) return;
    try {
      setIsManagingMemberId(userId);
      setMemberError(null);
      const res = await fetch(`/api/projects/${selectedProjectId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({ userId, role: 'contributor' }),
      });
      if (res.ok) {
        await fetchProjects();
      } else {
        const data = await res.json().catch(() => ({}));
        setMemberError(data.error || 'Failed to add member to project.');
      }
    } catch (err: any) {
      setMemberError(err?.message || 'Failed to add member to project.');
    } finally {
      setIsManagingMemberId(null);
    }
  };

  const handleRemoveProjectMember = async (memberId: string) => {
    if (!selectedProjectId || selectedProjectId === 'all' || !token) return;
    try {
      setIsManagingMemberId(memberId);
      setMemberError(null);
      const res = await fetch(`/api/projects/${selectedProjectId}/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        await fetchProjects();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to remove member. Only project leads or admins can remove members.');
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to remove member.');
    } finally {
      setIsManagingMemberId(null);
    }
  };

  const fetchDashboard = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/dashboard/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllTasks = async () => {
    if (!token) return;
    try {
      const url =
        selectedProjectId === 'all' ? '/api/tasks' : `/api/tasks?projectId=${selectedProjectId}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const loaded: Task[] = data.tasks || [];
        // One project is filtered by the server; several are picked out of the full list here
        setAllTasks(selectedProjectIds.length > 1 ? loaded.filter((t) => matchesProjectSelection(t.projectId)) : loaded);
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  };

  const fetchNextMeeting = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/meetings', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        const upcoming = (data.meetings || []).filter(
          (m: any) =>
            (m.status === 'scheduled' || m.status === 'live') &&
            m.id !== 'meet-strat-sync'
        );
        if (upcoming.length > 0) {
          setNextMeeting(upcoming[0]);
        } else {
          setNextMeeting(null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch meetings:', err);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchNextMeeting();
    fetchProjects();

    const handleProjectEvent = () => {
      fetchProjects();
    };
    window.addEventListener('project-created', handleProjectEvent);
    window.addEventListener('project-updated', handleProjectEvent);
    return () => {
      window.removeEventListener('project-created', handleProjectEvent);
      window.removeEventListener('project-updated', handleProjectEvent);
    };
  }, [token]);

  // Real-time synchronization for meetings
  useEffect(() => {
    const unsubscribe = subscribe((event) => {
      if (
        event.type === 'meeting_created' ||
        event.type === 'meeting_updated' ||
        event.type === 'meeting_cancelled'
      ) {
        fetchNextMeeting();
      }
    });
    return unsubscribe;
  }, [subscribe]);

  useEffect(() => {
    fetchAllTasks();
  }, [token, selectedProjectsKey]);

  // Real-time synchronization via DOM events (instant intra-tab) & WebSocket (cross-tab / cross-user)
  useEffect(() => {
    const handleTaskCreatedLocal = (e: any) => {
      const createdTask = e.detail;
      if (createdTask) {
        if (matchesProjectSelection(createdTask.projectId)) {
          setAllTasks((prev) => {
            if (prev.some((t) => t.id === createdTask.id)) return prev;
            return [createdTask, ...prev];
          });
        }
        fetchAllTasks();
        fetchDashboard();
      }
    };

    const handleTaskUpdatedLocal = (e: any) => {
      const updatedTask = e.detail;
      if (updatedTask) {
        setAllTasks((prev) =>
          prev.map((t) => (t.id === updatedTask.id ? { ...t, ...updatedTask } : t))
        );
        fetchAllTasks();
        fetchDashboard();
      }
    };

    const handleTaskArchivedLocal = (e: any) => {
      const { taskId } = e.detail || {};
      if (taskId) {
        setAllTasks((prev) => prev.filter((t) => t.id !== taskId));
        fetchDashboard();
      }
    };

    const handleRefreshAll = () => {
      fetchAllTasks();
      fetchDashboard();
    };

    window.addEventListener('task-created', handleTaskCreatedLocal);
    window.addEventListener('task-updated', handleTaskUpdatedLocal);
    window.addEventListener('task-archived', handleTaskArchivedLocal);
    window.addEventListener('task-restored', handleRefreshAll);
    window.addEventListener('project-created', handleRefreshAll);

    const unsubscribe = subscribe((event) => {
      if (event.type === 'task_created') {
        const createdTask = event.data;
        if (createdTask && matchesProjectSelection(createdTask.projectId)) {
          setAllTasks((prev) => {
            if (prev.some((t) => t.id === createdTask.id)) return prev;
            return [createdTask, ...prev];
          });
        }
        fetchAllTasks();
        fetchDashboard();
      } else if (event.type === 'task_updated') {
        const updatedTask = event.data;
        if (updatedTask) {
          setAllTasks((prev) =>
            prev.map((t) => (t.id === updatedTask.id ? { ...t, ...updatedTask } : t))
          );
        }
        fetchDashboard();
      } else if (event.type === 'task_archived') {
        const { taskId } = event.data || {};
        if (taskId) {
          setAllTasks((prev) => prev.filter((t) => t.id !== taskId));
        }
        fetchDashboard();
      } else if (
        event.type === 'task_restored' ||
        event.type === 'tasks_reordered' ||
        event.type === 'tasks_imported' ||
        event.type === 'project_created' ||
        event.type === 'project_updated' ||
        event.type === 'project_deleted'
      ) {
        fetchAllTasks();
        fetchDashboard();
      } else if (
        event.type === 'meeting_created' ||
        event.type === 'meeting_updated' ||
        event.type === 'meeting_cancelled'
      ) {
        fetchNextMeeting();
      }
    });

    return () => {
      window.removeEventListener('task-created', handleTaskCreatedLocal);
      window.removeEventListener('task-updated', handleTaskUpdatedLocal);
      window.removeEventListener('task-archived', handleTaskArchivedLocal);
      window.removeEventListener('task-restored', handleRefreshAll);
      window.removeEventListener('project-created', handleRefreshAll);
      unsubscribe();
    };
  }, [token, selectedProjectsKey, subscribe]);

  const handleUpdateTaskStatus = async (taskId: string, newStatus: TaskStatus) => {
    setAllTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
    );

    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-updated', { detail: data.task }));
        }
      }
      fetchDashboard();
      fetchAllTasks();
    } catch (err) {
      console.error('Failed to update task status:', err);
    }
  };

  const handleQuickCreateTask = async (status: TaskStatus, title: string) => {
    if (!token || !title.trim()) return;

    let targetProjId =
      selectedProjectId !== 'all'
        ? selectedProjectId
        : selectedProjectIds[0] || stats?.projects?.[0]?.id || allTasks[0]?.projectId || '';

    if (!targetProjId) {
      try {
        const projRes = await fetch('/api/projects?status=active&allSpaces=true', {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
        });
        if (projRes.ok) {
          const projData = await projRes.json().catch(() => ({}));
          targetProjId = projData.projects?.[0]?.id || '';
        }
      } catch {
        // ignore fallback fetch error
      }
    }

    if (!targetProjId) {
      setQuickCreateStatus(status);
      setIsCreateTaskModalOpen(true);
      return;
    }

    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          projectId: targetProjId,
          title: title.trim(),
          status,
          priority: 'medium',
          assigneeId: user?.id || null,
          assignorId: user?.id || null,
        }),
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          setAllTasks((prev) => {
            if (prev.some((t) => t.id === data.task.id)) return prev;
            return [data.task, ...prev];
          });
          window.dispatchEvent(new CustomEvent('task-created', { detail: data.task }));
        }
        fetchAllTasks();
        fetchDashboard();
      } else {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || 'Failed to create task.');
      }
    } catch (err) {
      console.error('Failed to quick create task:', err);
    }
  };

  // Current active project details & members (TSK-1039)
  const currentSelectedProject = useMemo(() => {
    if (selectedProjectId === 'all') return null;
    return projects.find((p) => p.id === selectedProjectId) || (stats?.projects || []).find((p) => p.id === selectedProjectId) || null;
  }, [projects, stats?.projects, selectedProjectId]);

  const projectMembers = useMemo(() => {
    return currentSelectedProject?.members || [];
  }, [currentSelectedProject]);

  // Extract distinct assignees/users scoped to selected project if one is active, or allTasks
  const availableUsers = useMemo(() => {
    const userMap = new Map<string, { id: string; fullName: string; count: number }>();
    let unassignedCount = 0;

    // Seed with project members if a specific project is selected
    if (selectedProjectId !== 'all' && projectMembers.length > 0) {
      projectMembers.forEach((m) => {
        userMap.set(m.id, {
          id: m.id,
          fullName: m.fullName || 'Member',
          count: 0,
        });
      });
    }

    allTasks.forEach((t) => {
      const hasAssignee = Boolean(t.assigneeId || (t.assignees && t.assignees.length > 0));
      if (!hasAssignee) {
        unassignedCount++;
      }
      if (t.assigneeId) {
        const existing = userMap.get(t.assigneeId);
        if (existing) {
          existing.count += 1;
        } else if (selectedProjectId === 'all') {
          userMap.set(t.assigneeId, {
            id: t.assigneeId,
            fullName: t.assigneeName || 'Assignee',
            count: 1,
          });
        }
      }
      if (t.assignees && t.assignees.length > 0) {
        t.assignees.forEach((a) => {
          const existing = userMap.get(a.id);
          if (existing) {
            existing.count += 1;
          } else if (selectedProjectId === 'all') {
            userMap.set(a.id, {
              id: a.id,
              fullName: a.fullName || 'Member',
              count: 1,
            });
          }
        });
      }
    });

    // With teams picked, only the people of those teams are listed (and unassigned cards are not shown)
    const people = Array.from(userMap.values());
    return {
      list: teamMemberIds ? people.filter((u) => teamMemberIds.has(u.id)) : people,
      unassignedCount: teamMemberIds ? 0 : unassignedCount,
    };
  }, [allTasks, selectedProjectId, projectMembers, teamMemberIds]);

  // Tasks filtered by user selection (MUST be called before early return)
  const userFilteredTasks = useMemo(() => {
    const teamTasks = teamMemberIds ? allTasks.filter((t) => taskInTeams(t, teamMemberIds)) : allTasks;
    if (selectedUserId === 'all') return teamTasks;
    return teamTasks.filter((t) => {
      if (selectedUserId === '__unassigned__') {
        return !t.assigneeId && (!t.assignees || t.assignees.length === 0);
      }
      return (
        t.assigneeId === selectedUserId ||
        (t.assignees && t.assignees.some((a) => a.id === selectedUserId))
      );
    });
  }, [allTasks, selectedUserId, teamMemberIds]);

  if (isLoading || !stats) {
    return (
      <div className="p-16 text-center text-xs text-[#9aa2b1] font-medium flex flex-col items-center justify-center space-y-3">
        <div className="w-8 h-8 border-3 border-brand-500/20 border-t-brand-500 rounded-full animate-spin" />
        <span>Loading workspace dashboard...</span>
      </div>
    );
  }

  // Calculate stage counts matching Kanban board stages (100% synchronized with Kanban columns)
  const isTaskOverdue = (t: Task) =>
    (t.status === 'overdue' || isOverdue(t.dueDate)) &&
    t.status !== 'done' &&
    t.status !== 'completed' &&
    t.status !== 'in_review';

  const overdueCount = userFilteredTasks.filter(isTaskOverdue).length;
  const todoCount = userFilteredTasks.filter((t) => (t.status === 'todo' || t.status === 'backlog') && !isTaskOverdue(t)).length;
  const inProgressCount = userFilteredTasks.filter((t) => t.status === 'in_progress' && !isTaskOverdue(t)).length;
  const inReviewCount = userFilteredTasks.filter((t) => t.status === 'in_review').length;
  const completedCount = userFilteredTasks.filter((t) => t.status === 'done' || t.status === 'completed').length;

  // Filter tasks to display on the Kanban/Multi-view board based on stage & user
  const displayedTasks =
    metricFilter === 'active'
      ? userFilteredTasks
      : filterByStage(userFilteredTasks, metricFilter as StageKey, hideOverdue, hideCompleted);

  const handleStageClick = (filterType: 'all' | 'todo' | 'in_progress' | 'in_review' | 'completed' | 'focus') => {
    setMetricFilter((prev: any) => (prev === filterType ? 'all' : filterType));
  };

  return (
    <div
      className={`animate-fadeIn ${
        dashboardViewType === 'kanban'
          ? 'flex-1 min-h-0 flex flex-col gap-2 overflow-hidden'
          : 'space-y-4 pb-8 overflow-y-auto flex-1 min-h-0 pr-1'
      }`}
    >
      {/* 1. Sleek Upcoming Meeting Section & AI Transcription Quick-Access Bar (Only renders if an actual meeting exists for this user) */}
      {nextMeeting && (
        <div className="bg-white dark:bg-[#181b20] rounded-xl px-3 py-2 border border-[#d5d8de]/60 dark:border-surface-800 shadow-2xs flex flex-wrap items-center justify-between gap-2 shrink-0">
          <div className="flex items-center space-x-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-[#0162fc] to-[#4b2efa] text-white flex items-center justify-center shrink-0">
              <Video className="w-3.5 h-3.5 stroke-[2.5]" />
            </div>

            <div className="min-w-0 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="text-[10px] font-mono font-bold px-1.5 py-0.2 rounded-full bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-300 border border-brand-200 dark:border-brand-800">
                {nextMeeting.clientName || 'Scheduled Meeting'}
              </span>
              <h3 className="font-extrabold text-xs text-[#121417] dark:text-white truncate">
                {nextMeeting.title}
              </h3>
              <span className="text-[11px] text-[#667085] dark:text-surface-400">
                •{' '}
                {nextMeeting.scheduledAt
                  ? new Date(nextMeeting.scheduledAt).toLocaleDateString([], {
                      month: 'short',
                      day: 'numeric',
                    }) +
                    ' @ ' +
                    new Date(nextMeeting.scheduledAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Upcoming'}{' '}
                ({nextMeeting.durationMinutes || 45}m)
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={async () => {
                // Open (or create) the meeting's chat room so in-call chat works
                let channelId: string | undefined;
                try {
                  const res = await fetch(`/api/meetings/${nextMeeting.id}/chat-channel`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (res.ok) channelId = (await res.json()).channelId;
                } catch (err) {
                  console.warn('Could not open the meeting chat room:', err);
                }
                joinCall({ title: nextMeeting.title, type: 'group', channelId });
              }}
              className="px-2.5 py-1 rounded-full bg-gradient-to-r from-[#0162fc] to-[#4b2efa] text-white text-[11px] font-bold flex items-center space-x-1"
            >
              <Video className="w-3 h-3 fill-current" />
              <span>Join Call</span>
            </button>
            <button
              onClick={() => onNavigate('meetings')}
              className="px-2.5 py-1 rounded-full bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 text-surface-900 dark:text-surface-100 text-[11px] font-bold border border-surface-200 dark:border-surface-700 flex items-center space-x-1"
            >
              <span>Meetings</span>
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* 2. Kanban Stage Quick Filter Pills & Import Action (sits below the meeting bar) */}
      <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 px-0.5 shrink-0">
        <StageFilterBar
          tasks={userFilteredTasks}
          stage={metricFilter as StageKey}
          onSelectStage={handleStageClick}
          hideOverdue={hideOverdue}
          hideCompleted={hideCompleted}
          onToggleHideOverdue={toggleHideOverdue}
          onToggleHideCompleted={toggleHideCompleted}
        />

        {/* Quick Action: Import Tasks */}
        <div className="flex items-center shrink-0">
          <button
            onClick={() => setIsImportTasksOpen(true)}
            className="h-7 px-2.5 rounded-full bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-[11px] font-bold flex items-center space-x-1 shadow-2xs transition-transform hover:scale-105 active:scale-95"
            title="Import tasks from Excel or CSV spreadsheet"
          >
            <FileSpreadsheet className="w-3 h-3 text-emerald-600 dark:text-emerald-400 stroke-[2.5]" />
            <span className="hidden xs:inline">Import Excel / CSV</span>
            <span className="xs:hidden">Import</span>
          </button>
        </div>
      </div>

      {/* 3. The Kanban Board System (Comes immediately after the Meeting section) */}
      <div
        id="kanban-board-section"
        className={`bg-white dark:bg-[#181b20] rounded-2xl p-2 sm:p-2.5 border border-[#d5d8de]/60 dark:border-surface-800 shadow-neo ${
          dashboardViewType === 'kanban' ? 'flex-1 min-h-0 flex flex-col gap-1.5 overflow-hidden' : 'space-y-2.5'
        }`}
      >
        {/* Header bar with View Selector, Project filter, and User filter */}
        <div className="flex flex-nowrap items-center justify-between gap-2 pb-1 border-b border-[#eaecf0] dark:border-surface-800 shrink-0">
          <div className="flex items-center space-x-2 min-w-0 shrink">
            <div className="w-7 h-7 rounded-xl bg-[#121417] text-brand-400 flex items-center justify-center shrink-0">
              {dashboardViewType === 'kanban' && <LayoutGrid className="w-3.5 h-3.5" />}
              {dashboardViewType === 'list' && <List className="w-3.5 h-3.5" />}
              {dashboardViewType === 'timeline' && <Clock className="w-3.5 h-3.5" />}
              {dashboardViewType === 'whiteboard' && <Layout className="w-3.5 h-3.5" />}
              {dashboardViewType === 'calendar' && <CalendarIcon className="w-3.5 h-3.5" />}
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="font-extrabold text-sm text-[#121417] dark:text-white truncate min-w-0">
                {dashboardViewType === 'kanban' && 'Sprint Kanban Board'}
                {dashboardViewType === 'list' && 'Grid & Dense Spreadsheet'}
                {dashboardViewType === 'timeline' && 'Interactive Gantt & Critical Path'}
                {dashboardViewType === 'whiteboard' && 'Mind Map & Freeform Brainstorm Canvas'}
                {dashboardViewType === 'calendar' && 'Sprint Milestones Calendar'}
              </h3>
              <span className="text-[11px] font-bold px-2 py-0.2 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300 shrink-0 min-w-[64px] text-center">
                {displayedTasks.length} cards
              </span>
              {metricFilter !== 'all' && (
                <button
                  onClick={() => setMetricFilter('all')}
                  className="flex items-center space-x-1 text-[10px] font-bold px-2 py-0.2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-black hover:opacity-80 transition-opacity shrink-0"
                  title="Click to clear filter and show all tasks"
                >
                  <span>
                    Filtered: {metricFilter === 'active' && (isLead ? 'Active Deals' : 'My Active')}
                    {metricFilter === 'completed' && (isLead ? 'Milestones Won' : 'My Completed')}
                    {metricFilter === 'focus' && (isLead ? 'Needs Focus' : 'Needs My Focus')}
                  </span>
                  <X className="w-2.5 h-2.5 ml-0.5" />
                </button>
              )}
            </div>
          </div>

          {/* View Mode Dropdown + Project Filter + User Filter Selectors (ALL ON SAME LINE) */}
          <div className="flex flex-nowrap items-center gap-1.5 shrink-0">
            {/* View Selector Dropdown */}
            <div data-tour="multiview-selector" className="flex items-center space-x-1">
              <span className="text-[11px] font-bold text-[#667085] dark:text-[#9aa2b1]">View:</span>
              <div className="relative">
                <select
                  value={dashboardViewType}
                  onChange={(e) => setDashboardViewType(e.target.value as any)}
                  className="text-[11px] font-black pl-2 pr-6 py-1 rounded-xl bg-[#121417] text-white dark:bg-white dark:text-black border border-transparent shadow-sm focus:outline-none cursor-pointer appearance-none"
                  aria-label="Select Dashboard View"
                >
                  <option value="kanban">📌 Kanban Board</option>
                  <option value="list">📊 Grid / Spreadsheet</option>
                  <option value="timeline">⏱️ Gantt & Timeline</option>
                  <option value="whiteboard">🧠 Mind Map / Canvas</option>
                  <option value="calendar">📅 Calendar View</option>
                </select>
                <ChevronDown className="w-3 h-3 text-brand-400 dark:text-brand-300 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none stroke-[2.5]" />
              </div>
            </div>

            {/* Team filter: the tasks of the people in one or more teams */}
            <TeamSelect
              selectedTeamIds={selectedTeamIds}
              onChange={(ids) => {
                setSelectedTeamIds(ids);
                setSelectedUserId('all');
              }}
            />

            {/* Project Filter Selector */}
            <ProjectMultiSelect
              projects={(projects.length > 0 ? projects : stats?.projects || []).map((p: any) => ({
                id: p.id,
                name: p.name,
                key: p.key,
              }))}
              selectedIds={selectedProjectIds}
              onChange={(ids) => {
                setSelectedProjectIds(ids);
                setSelectedUserId('all');
              }}
            />

            {/* Project Member Avatar Stack & Add Member (TSK-1039 & TSK-1038); the spot is kept even with no
                project picked, so choosing a project does not push the other filters around */}
            <div className="w-[128px] shrink-0 flex items-center">
            {currentSelectedProject && (
              <div className="flex items-center space-x-1 pl-1 border-l border-[#d5d8de] dark:border-surface-700 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsViewMembersModalOpen(true)}
                  className="flex items-center -space-x-1.5 cursor-pointer hover:opacity-85 transition-opacity"
                  title={`View ${projectMembers.length} members of ${currentSelectedProject.name} — Click to manage`}
                >
                  {projectMembers.slice(0, 4).map((m) => (
                    <Avatar
                      key={m.id}
                      src={m.avatarUrl}
                      name={m.fullName}
                      size="xs"
                      className="ring-2 ring-white dark:ring-surface-900"
                    />
                  ))}
                  {projectMembers.length > 4 && (
                    <span className="w-5 h-5 rounded-full bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-200 text-[10px] font-black flex items-center justify-center ring-2 ring-white dark:ring-surface-900">
                      +{projectMembers.length - 4}
                    </span>
                  )}
                  {projectMembers.length === 0 && (
                    <span className="text-[10px] text-surface-400 font-medium px-1 flex items-center space-x-1">
                      <Users className="w-3 h-3 text-surface-400" />
                      <span>0 members</span>
                    </span>
                  )}
                </button>

                {(isLead || currentSelectedProject.ownerId === user?.id) && (
                  <button
                    type="button"
                    onClick={() => {
                      fetchUsers();
                      setIsAddMemberModalOpen(true);
                    }}
                    className="p-1 rounded-lg bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-600 dark:text-surface-300 transition-colors"
                    title={`Add member to ${currentSelectedProject.name}`}
                  >
                    <UserPlus className="w-3 h-3 text-brand-600 dark:text-brand-400 stroke-[2.5]" />
                  </button>
                )}
              </div>
            )}
            </div>

            {/* User Filter Dropdown */}
            <div className="flex items-center space-x-1 bg-[#f4f5f7] dark:bg-surface-800 rounded-xl px-2 py-1 border border-[#d5d8de] dark:border-surface-700">
              <Users className="w-3 h-3 text-[#667085] dark:text-surface-400 shrink-0" />
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="bg-transparent text-[11px] font-bold text-[#121417] dark:text-white focus:outline-none cursor-pointer w-[150px] truncate"
                title="Filter tasks by user"
              >
                <option value="all">
                  {selectedProjectId !== 'all' && currentSelectedProject
                    ? `Project Members (${availableUsers.list.length})`
                    : `All Users (${availableUsers.list.length})`}
                </option>
                {user && (
                  <option value={user.id}>
                    Assigned to Me ({allTasks.filter((t) => t.assigneeId === user.id || (t.assignees && t.assignees.some((a) => a.id === user.id))).length})
                  </option>
                )}
                {availableUsers.unassignedCount > 0 && (
                  <option value="__unassigned__">
                    Unassigned ({availableUsers.unassignedCount})
                  </option>
                )}
                {availableUsers.list
                  .filter((u) => !user || u.id !== user.id)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.fullName} ({u.count})
                    </option>
                  ))}
              </select>
              {/* always takes its space, so picking a user does not move anything */}
              <button
                type="button"
                onClick={() => setSelectedUserId('all')}
                className={`p-0.5 rounded text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 transition-colors ${
                  selectedUserId !== 'all' ? '' : 'invisible'
                }`}
                title="Clear user filter"
                tabIndex={selectedUserId !== 'all' ? 0 : -1}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>

            {can('task.create') && (
              <button
                type="button"
                onClick={() => {
                  setQuickCreateStatus('todo');
                  setIsCreateTaskModalOpen(true);
                }}
                className="px-2.5 py-1 rounded-xl bg-[#121417] hover:bg-black text-white dark:bg-white dark:text-black text-[11px] font-bold flex items-center space-x-1 shadow-2xs transition-transform hover:scale-105 shrink-0 cursor-pointer"
                title="Create a new task"
              >
                <Plus className="w-3 h-3 text-brand-400 dark:text-brand-600 stroke-[3]" />
                <span>Task</span>
              </button>
            )}
          </div>
        </div>

        {/* View Component Switcher */}
        <div className={dashboardViewType === 'kanban' ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : 'pt-1'}>
          {dashboardViewType === 'kanban' && (
            <KanbanBoard
              tasks={displayedTasks}
              onTaskClick={(task) => onOpenTask(task.id, task.projectId)}
              onUpdateTaskStatus={handleUpdateTaskStatus}
              onQuickCreateTask={handleQuickCreateTask}
              hideUserFilter={true}
              hideOverdue={hideOverdue}
              hideCompleted={hideCompleted}
            />
          )}

          {dashboardViewType === 'list' && (
            <TaskTable
              tasks={displayedTasks}
              onTaskClick={(task) => onOpenTask(task.id, task.projectId)}
              onUpdateTaskStatus={handleUpdateTaskStatus}
              onQuickCreateTask={handleQuickCreateTask}
            />
          )}

          {dashboardViewType === 'timeline' && (
            <TimelineView
              tasks={displayedTasks}
              projectId={selectedProjectId !== 'all' ? selectedProjectId : selectedProjectIds[0] || stats?.projects?.[0]?.id}
              onTaskClick={(task) => onOpenTask(task.id, task.projectId)}
            />
          )}

          {dashboardViewType === 'whiteboard' && (
            <WhiteboardView
              projectId={
                selectedProjectId !== 'all' ? selectedProjectId : selectedProjectIds[0] || stats?.projects?.[0]?.id || 'proj-1'
              }
              tasks={displayedTasks}
              onTaskClick={(task) => onOpenTask(task.id, task.projectId)}
              onConvertNoteToTask={() => {
                fetchAllTasks();
                fetchDashboard();
              }}
            />
          )}

          {dashboardViewType === 'calendar' && (
            <CalendarView
              tasks={displayedTasks}
              onTaskClick={(task) => onOpenTask(task.id, task.projectId)}
            />
          )}
        </div>
      </div>

      {/* Quick Create Task Modal */}
      <CreateTaskModal
        isOpen={isCreateTaskModalOpen}
        onClose={() => setIsCreateTaskModalOpen(false)}
        initialStatus={quickCreateStatus}
        projectId={selectedProjectId !== 'all' ? selectedProjectId : undefined}
        onTaskCreated={() => {
          fetchAllTasks();
          fetchDashboard();
        }}
      />

      {/* Bulk Import Tasks Modal */}
      <ImportTasksModal
        isOpen={isImportTasksOpen}
        onClose={() => setIsImportTasksOpen(false)}
        projectId={selectedProjectId !== 'all' ? selectedProjectId : (selectedProjectIds[0] || stats?.projects?.[0]?.id)}
        projects={stats?.projects || []}
        onTasksImported={() => {
          fetchAllTasks();
          fetchDashboard();
        }}
      />
      {/* Existing Project Members Modal (TSK-1039) */}
      {isViewMembersModalOpen && currentSelectedProject && (
        <ExistingProjectMembersModal
          isOpen={isViewMembersModalOpen}
          onClose={() => setIsViewMembersModalOpen(false)}
          projectName={currentSelectedProject.name}
          members={projectMembers}
          onAddMemberClick={() => {
            fetchUsers();
            setIsAddMemberModalOpen(true);
          }}
          onFilterByMember={(memberId) => setSelectedUserId(memberId)}
          onRemoveMember={handleRemoveProjectMember}
          canManageMembers={
            isLead ||
            currentSelectedProject.ownerId === user?.id
          }
        />
      )}

      {/* Add / Manage Project Members Modal (TSK-1038) */}
      {isAddMemberModalOpen && currentSelectedProject && (
        <Modal
          isOpen={isAddMemberModalOpen}
          onClose={() => {
            setIsAddMemberModalOpen(false);
            setMemberError(null);
            setMemberSearchQuery('');
          }}
          title={`Manage Project Members • ${currentSelectedProject.name}`}
          maxWidth="md"
        >
          <div className="space-y-4">
            <p className="text-xs text-surface-500">
              Add colleagues to this project so they can be assigned tasks, view board status, and collaborate.
            </p>

            {memberError && (
              <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-xs text-rose-600 font-medium">
                {memberError}
              </div>
            )}

            <div className="relative">
              <Search className="w-3.5 h-3.5 text-surface-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search colleagues by name or email..."
                value={memberSearchQuery}
                onChange={(e) => setMemberSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
              {isLoadingAllUsers && allUsers.length === 0 ? (
                <div className="py-8 text-center text-xs text-surface-400 flex flex-col items-center justify-center space-y-2">
                  <Loader2 className="w-5 h-5 animate-spin text-brand-500" />
                  <span>Loading team members...</span>
                </div>
              ) : allUsers.filter((u) => {
                  if (!memberSearchQuery.trim()) return true;
                  const q = memberSearchQuery.toLowerCase();
                  return (
                    (u.fullName && u.fullName.toLowerCase().includes(q)) ||
                    (u.email && u.email.toLowerCase().includes(q))
                  );
                }).length === 0 ? (
                <div className="py-8 text-center text-xs text-surface-400 space-y-2">
                  <p>
                    {memberSearchQuery.trim()
                      ? `No colleagues found matching "${memberSearchQuery}".`
                      : 'No colleagues found in workspace.'}
                  </p>
                  <button
                    type="button"
                    onClick={fetchUsers}
                    className="text-brand-600 hover:text-brand-700 underline text-xs font-semibold"
                  >
                    Refresh member list
                  </button>
                </div>
              ) : (
                allUsers
                  .filter((u) => {
                    if (!memberSearchQuery.trim()) return true;
                    const q = memberSearchQuery.toLowerCase();
                    return (
                      (u.fullName && u.fullName.toLowerCase().includes(q)) ||
                      (u.email && u.email.toLowerCase().includes(q))
                    );
                  })
                  .map((u) => {
                    const isAlreadyMember = projectMembers.some((m) => m.id === u.id);

                    return (
                      <div
                        key={u.id}
                        className="p-2.5 rounded-xl bg-surface-50 dark:bg-surface-800/60 border border-surface-200/60 dark:border-surface-700/60 flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center space-x-2.5 min-w-0 pr-2">
                          <Avatar src={u.avatarUrl} name={u.fullName} size="sm" />
                          <div className="min-w-0 truncate">
                            <div className="font-semibold text-surface-900 dark:text-surface-100 truncate">
                              {u.fullName}
                            </div>
                            <div className="text-[11px] text-surface-400 truncate">{u.email}</div>
                          </div>
                        </div>

                        {isAlreadyMember ? (
                          <div className="flex items-center space-x-2 shrink-0">
                            <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold">
                              Member
                            </span>
                            {(isLead ||
                              currentSelectedProject.ownerId === user?.id) && (
                              <button
                                type="button"
                                disabled={isManagingMemberId === u.id}
                                onClick={() => handleRemoveProjectMember(u.id)}
                                className="px-2 py-1 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 text-[11px] font-semibold transition-colors disabled:opacity-50"
                              >
                                {isManagingMemberId === u.id ? 'Removing...' : 'Remove'}
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={isManagingMemberId === u.id}
                            onClick={() => handleAddProjectMember(u.id)}
                            className="px-3 py-1 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-semibold text-[11px] transition-colors shrink-0 disabled:opacity-50"
                          >
                            {isManagingMemberId === u.id ? 'Adding...' : 'Add'}
                          </button>
                        )}
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

